/**
 * Dealer Overview Tab
 * Phase 2E: Master Admin UI
 * Phase 2E-P0: Added embed verification helper
 * 
 * Features:
 * - Public key display with copy
 * - Embed snippet with copy
 * - Stats (users, leads)
 * - Verify embed configuration button
 * - Reseller Economics section
 */
import { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import ResellerEconomicsSection from "./ResellerEconomicsSection";
import franchiseMakesModels from "../../data/franchise_makes_models.json";

const DealerOverviewTab = ({ dealer, onRefresh }) => {
  const { apiFetch } = useAuth();
  const { success: showToast, error: showError } = useToast();
  const [copiedField, setCopiedField] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    showToast(`${field} copied to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleVerifyEmbed = async () => {
    try {
      setVerifying(true);
      setVerifyResult(null);
      const response = await apiFetch(`/admin/dealers/${dealer.id}/verify-embed`);
      setVerifyResult(response.data);
    } catch (err) {
      showError("Failed to verify embed configuration");
      setVerifyResult({ verification_status: "error", warnings: ["Failed to verify configuration"] });
    } finally {
      setVerifying(false);
    }
  };

  const stats = [
    { label: "Users", value: dealer.user_count || 0, icon: "👥" },
    { label: "Leads", value: dealer.lead_count || 0, icon: "📈" },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="text-2xl">{stat.icon}</div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="text-2xl">🔓</div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {dealer.unlock_settings?.enabled ? "On" : "Off"}
                </div>
                <div className="text-sm text-gray-500">Unlock Feature</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="text-2xl">{dealer.is_active ? "✅" : "⛔"}</div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {dealer.is_active ? "Active" : "Disabled"}
                </div>
                <div className="text-sm text-gray-500">Status</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Public Key */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">🔑 Public Key</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <code className="flex-1 bg-gray-100 px-4 py-3 rounded-lg font-mono text-sm break-all">
              {dealer.public_key}
            </code>
            <Button
              variant="outline"
              onClick={() => copyToClipboard(dealer.public_key, "Public Key")}
              className="shrink-0"
            >
              {copiedField === "Public Key" ? "✓ Copied" : "📋 Copy"}
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            This key uniquely identifies this dealer and is used in the embed snippet.
          </p>
        </CardContent>
      </Card>

      {/* Embed Snippet */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">📝 Embed Snippet</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="bg-gray-900 text-green-400 px-4 py-4 rounded-lg font-mono text-xs overflow-x-auto whitespace-pre-wrap">
              {dealer.embed_snippet || generateEmbedSnippet(dealer.public_key)}
            </pre>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(dealer.embed_snippet || generateEmbedSnippet(dealer.public_key), "Embed Snippet")}
              className="absolute top-2 right-2 bg-gray-800 hover:bg-gray-700 text-white border-gray-700"
            >
              {copiedField === "Embed Snippet" ? "✓ Copied" : "📋 Copy"}
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-3">
            Add this snippet to the dealer&apos;s website to enable the Unlock Offer feature.
            Place it before the closing <code className="bg-gray-100 px-1 rounded">&lt;/body&gt;</code> tag.
          </p>
        </CardContent>
      </Card>

      {/* Embed Verification Helper */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">🔍 Verify Embed Installation</CardTitle>
          <CardDescription>
            Test the embed configuration to ensure it will work correctly on the dealer&apos;s website.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              onClick={handleVerifyEmbed}
              disabled={verifying}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {verifying ? "Verifying..." : "Verify Configuration"}
            </Button>

            {verifyResult && (
              <div className={`rounded-lg p-4 ${
                verifyResult.verification_status === "ok" 
                  ? "bg-green-50 border border-green-200" 
                  : "bg-yellow-50 border border-yellow-200"
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={
                    verifyResult.verification_status === "ok"
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }>
                    {verifyResult.verification_status === "ok" ? "✓ Ready" : "⚠ Warning"}
                  </Badge>
                  <span className="text-sm font-medium">
                    {verifyResult.verification_status === "ok" 
                      ? "Embed is configured correctly" 
                      : "Configuration needs attention"}
                  </span>
                </div>

                {verifyResult.warnings && verifyResult.warnings.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Warnings:</p>
                    <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                      {verifyResult.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {verifyResult.embed_config && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">Configuration:</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-600">Enabled:</div>
                      <div className="font-medium">{verifyResult.embed_config.enabled ? "Yes" : "No"}</div>
                      
                      <div className="text-gray-600">Headline:</div>
                      <div className="font-medium truncate">{verifyResult.embed_config.headline_text || "Not set"}</div>
                      
                      <div className="text-gray-600">Button Text:</div>
                      <div className="font-medium">{verifyResult.embed_config.button_text || "Not set"}</div>
                      
                      <div className="text-gray-600">Primary Color:</div>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded border" 
                          style={{ backgroundColor: verifyResult.embed_config.primary_color || "#2563eb" }}
                        />
                        <span className="font-mono text-xs">{verifyResult.embed_config.primary_color || "#2563eb"}</span>
                      </div>
                      
                      <div className="text-gray-600">SMS Verification:</div>
                      <div className="font-medium">{verifyResult.embed_config.require_sms_verification ? "Required" : "Optional"}</div>
                    </div>
                    
                    {verifyResult.updated_at && (
                      <p className="text-xs text-gray-500 mt-3">
                        Last updated: {new Date(verifyResult.updated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Description */}
      {dealer.description && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">{dealer.description}</p>
          </CardContent>
        </Card>
      )}
      
      {/* P2: Dealer Info Section */}
      <DealerInfoSection dealer={dealer} onRefresh={onRefresh} />
      
      {/* Reseller Economics */}
      <ResellerEconomicsSection dealerId={dealer.id} />
    </div>
  );
};

// P2: Dealer Info Section Component
const DealerInfoSection = ({ dealer, onRefresh }) => {
  const { apiFetch } = useAuth();
  const { success: showToast, error: showError } = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    dealer_name: dealer.dealer_name || dealer.name || '',
    dealer_address: dealer.dealer_address || '',
    dealer_phone: dealer.dealer_phone || '',
    contact_name: dealer.contact_name || '',
    contact_email: dealer.contact_email || '',
    contact_phone: dealer.contact_phone || '',
    franchise_make: dealer.franchise_make || '',
  });

  // Re-sync formData whenever the dealer prop refreshes (e.g. after a successful
  // save → onRefresh fetches the updated record). Only resync when NOT editing,
  // otherwise the user's in-flight edits would be wiped out.
  useEffect(() => {
    if (editing) return;
    setFormData({
      dealer_name: dealer.dealer_name || dealer.name || '',
      dealer_address: dealer.dealer_address || '',
      dealer_phone: dealer.dealer_phone || '',
      contact_name: dealer.contact_name || '',
      contact_email: dealer.contact_email || '',
      contact_phone: dealer.contact_phone || '',
      franchise_make: dealer.franchise_make || '',
    });
  }, [
    editing,
    dealer.dealer_name, dealer.name, dealer.dealer_address, dealer.dealer_phone,
    dealer.contact_name, dealer.contact_email, dealer.contact_phone, dealer.franchise_make,
  ]);
  const franchiseMakes = useMemo(
    () => Object.keys(franchiseMakesModels).sort((a, b) => a.localeCompare(b)),
    []
  );

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await apiFetch(`/admin/dealers/${dealer.id}/info`, {
        method: 'PATCH',
        data: formData,
      });
      const updated = res.data?.updated_fields || [];
      showToast(
        updated.length
          ? `Saved: ${updated.join(', ')}`
          : 'Dealer info updated'
      );
      setEditing(false);
      if (onRefresh) await onRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.message
        || 'Unknown error';
      showError(`Failed to save: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">🏢 Dealer Info</CardTitle>
          <CardDescription>Contact and location information</CardDescription>
        </div>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            ✏️ Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Name</label>
                <input
                  type="text"
                  value={formData.dealer_name}
                  onChange={(e) => setFormData({ ...formData, dealer_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ABC Motors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Phone</label>
                <input
                  type="tel"
                  value={formData.dealer_phone}
                  onChange={(e) => setFormData({ ...formData, dealer_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Address</label>
              <input
                type="text"
                value={formData.dealer_address}
                onChange={(e) => setFormData({ ...formData, dealer_address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123 Main St, City, State 12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Franchise Make <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.franchise_make}
                onChange={(e) => setFormData({ ...formData, franchise_make: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="overview-franchise-make-select"
              >
                <option value="">Select franchise make…</option>
                <option value="USED_CARS_ONLY">Used Cars Only (No Franchise)</option>
                <optgroup label="Franchise Makes">
                  {franchiseMakes.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </optgroup>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Required to enable New Vehicle Pricing Rules. &ldquo;Used Cars
                Only&rdquo; hides the New pricing tab entirely.
              </p>
            </div>
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Primary Store Contact</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="john@dealer.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Dealer Name</p>
                <p className="font-medium">{dealer.dealer_name || dealer.name || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Dealer Phone</p>
                <p className="font-medium">{dealer.dealer_phone || '—'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Dealer Address</p>
              <p className="font-medium">{dealer.dealer_address || '—'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Franchise Make</p>
              {dealer.franchise_make === "USED_CARS_ONLY" ? (
                <p className="font-medium text-gray-800" data-testid="overview-franchise-make-value">
                  Used Cars Only · <span className="text-xs text-gray-500">No franchise</span>
                </p>
              ) : dealer.franchise_make ? (
                <p className="font-medium" data-testid="overview-franchise-make-value">{dealer.franchise_make}</p>
              ) : (
                <p className="font-medium text-amber-700" data-testid="overview-franchise-make-missing">
                  ⚠ Not Set — required for New Vehicle Pricing Rules
                </p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Dealer Group</p>
              {dealer.dealer_group_id ? (
                <p className="font-medium text-purple-700" data-testid="overview-dealer-group-value">
                  Part of a Dealer Group · users with rooftop access can switch into this rooftop
                </p>
              ) : (
                <p className="font-medium text-gray-500" data-testid="overview-dealer-group-none">
                  Not in a Dealer Group
                </p>
              )}
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Primary Store Contact</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{dealer.contact_name || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{dealer.contact_email || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{dealer.contact_phone || '—'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Helper to generate embed snippet if not provided by API
// CANONICAL: Always use app.verifieddemand.com for dealer-facing snippets (white-label requirement)
const EMBED_BASE_URL = "https://app.verifieddemand.com";

const generateEmbedSnippet = (publicKey) => {
  return `<!-- Verified Demand Embed -->
<script
  src="${EMBED_BASE_URL}/api/embed-nocache.js"
  data-public-key="${publicKey}"
  async>
</script>`;
};

export default DealerOverviewTab;
